import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { retry } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import {
  ToolsService,
  ProjectSearchRequest,
  ProjectSearchResult,
} from '../../services/tools.service';
import { LookupService } from '../../services/lookup.service';

export interface SearchField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'autocomplete';
  operators: { key: string; label: string }[];
  step?: string;
  options?: { id: number; name: string }[];
}

export interface ActiveFilter {
  field: SearchField;
  operator: string;
  operatorLabel: string;
  value: string;
  valueTo?: string;
  displayValue: string;
}

const TEXT_OPS   = [{ key: 'contains', label: 'contains' }, { key: 'is', label: 'is' }, { key: 'startsWith', label: 'starts with' }];
const LIST_OPS   = [{ key: 'is', label: 'is' }];
const NUM_OPS  = [
  { key: 'is',        label: 'is' },
  { key: 'gt',        label: 'greater than' },
  { key: 'gte',       label: 'greater or equal than' },
  { key: 'lt',        label: 'less than' },
  { key: 'lte',       label: 'less or equal than' },
  { key: 'between',   label: 'between (inclusive)' },
  { key: 'betweenEx', label: 'between (exclusive)' },
];
const DATE_OPS = [
  { key: 'is',        label: 'is' },
  { key: 'gt',        label: 'greater than' },
  { key: 'gte',       label: 'greater or equal than' },
  { key: 'lt',        label: 'less than' },
  { key: 'lte',       label: 'less or equal than' },
  { key: 'between',   label: 'between (inclusive)' },
  { key: 'betweenEx', label: 'between (exclusive)' },
];

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css'],
})
export class SearchComponent implements OnInit {

  readonly fields: SearchField[] = [
    { key: 'projectName',               label: 'Project Name',         type: 'text',          operators: TEXT_OPS },
    { key: 'wellLocation',              label: 'Well Location',        type: 'text',          operators: TEXT_OPS },
    { key: 'wellName',                  label: 'Well Name',            type: 'text',          operators: TEXT_OPS },
    { key: 'pipeSize',                  label: 'Pipe Size',            type: 'text',          operators: TEXT_OPS },
    { key: 'rigName',                   label: 'Rig Name',             type: 'text',          operators: TEXT_OPS },
    { key: 'md',                        label: 'Measured Depth',       type: 'number',        operators: NUM_OPS },
    { key: 'tvd',                       label: 'Total Vertical Depth', type: 'number',        operators: NUM_OPS },
    { key: 'bhct',                      label: 'BHCT',                 type: 'number',        operators: NUM_OPS },
    { key: 'bhst',                      label: 'BHST',                 type: 'number',        operators: NUM_OPS },
    { key: 'finalPressure',             label: 'Final Pressure',       type: 'number',        operators: NUM_OPS },
    { key: 'density',                   label: 'Density',              type: 'number',        operators: NUM_OPS },
    { key: 'receiveDate',               label: 'Receive Date',         type: 'date',          operators: DATE_OPS },
    { key: 'dueDate',                   label: 'Due Date',             type: 'date',          operators: DATE_OPS }, 
    { key: 'jobType',                   label: 'Job Type',             type: 'select',        operators: LIST_OPS, options: [] },   
    { key: 'serviceCompany',            label: 'Service Company',      type: 'select',        operators: LIST_OPS, options: [] },
    { key: 'thickeningTime',            label: 'Thickening Time',      type: 'number',        operators: NUM_OPS },
    { key: 'testType',                  label: 'Test Type',            type: 'select',        operators: LIST_OPS, options: [] },
    { key: 'material',                  label: 'Material',             type: 'autocomplete',  operators: LIST_OPS },
  ];

  selectedField: SearchField | null = null;
  selectedOperator: string = '';
  filterValue: string = '';
  filterValueTo: string = '';

  jobTypeOptions: { id: number; name: string }[] = [];
  jobTypesLoading = false;
  jobTypesError: string | null = null;

  testTypeOptions: { id: number; name: string }[] = [];
  testTypesLoading = false;
  testTypesError: string | null = null;

  serviceCompanyOptions: { id: number; name: string }[] = [];
  serviceCompaniesLoading = false;
  serviceCompaniesError: string | null = null;

  // autocomplete state
  materialSuggestions: string[] = [];
  showSuggestions = false;
  private allMaterials: string[] = [];
  private materialSelected = false;

  activeFilters: ActiveFilter[] = [];

  results: ProjectSearchResult[] = [];
  isSearching: boolean = false;
  errorMessage: string | null = null;
  searched: boolean = false;

  sortKey: keyof ProjectSearchResult | null = null;
  sortDir: 'asc' | 'desc' = 'asc';

  get sortedResults(): ProjectSearchResult[] {
    if (!this.sortKey) return this.results;
    const key = this.sortKey;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.results].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'boolean') return (av === bv ? 0 : av ? -1 : 1) * dir;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }

  getStatusClass(status: string): string {
    switch ((status ?? '').toLowerCase().replace(/\s+/g, '')) {
      case 'completed':       return 'status-completed';
      case 'draft':           return 'status-draft';
      case 'draftagain':      return 'status-draft';
      case 'ongoinginlab':    return 'status-ongoing-lab';
      case 'finishedinlab':   return 'status-finished-lab';
      case 'senttolab':       return 'status-sent-lab';
      case 'senttolabagain':  return 'status-sent-lab';
      case 'hold':            return 'status-hold';
      case 'ongoing':         return 'status-ongoing';
      default:                return 'status-unknown';
    }
  }

  setSort(key: keyof ProjectSearchResult): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
  }

  constructor(
    private toolsService: ToolsService,
    private lookupService: LookupService,
    private dialogRef: MatDialogRef<SearchComponent>,
  ) {}

  ngOnInit(): void {
    this.loadMaterials();
    this.loadJobTypes();
    this.loadTestTypes();
    this.loadServiceCompanies();
  }



  selectSuggestion(name: string): void {
    this.filterValue = name;
    this.materialSelected = true;
    this.materialSuggestions = [];
    this.showSuggestions = false;
  }

  @HostListener('document:click')
  closeSuggestions(): void {
    this.showSuggestions = false;
  }

  onMaterialInput(value: string): void {
    this.materialSelected = false;
    const q = value.trim().toLowerCase();
    if (!q) {
      this.materialSuggestions = [];
      this.showSuggestions = false;
      return;
    }
    this.materialSuggestions = this.allMaterials.filter(m => m.toLowerCase().includes(q));
    this.showSuggestions = this.materialSuggestions.length > 0;
  }

  onFieldChange(): void {
    this.selectedOperator = '';
    this.filterValue = '';
    this.filterValueTo = '';
    this.materialSuggestions = [];
    this.showSuggestions = false;
    this.materialSelected = false;
    if (this.selectedField && this.selectedField.operators.length === 1) {
      this.selectedOperator = this.selectedField.operators[0].key;
    }    
  }

  isBetween(): boolean {
    return this.selectedOperator === 'between' || this.selectedOperator === 'betweenEx';
  }

  private hasValue(v: string | number | null | undefined): boolean {
    return v !== null && v !== undefined && String(v).trim() !== '';
  }

  canAddFilter(): boolean {
    if (!this.selectedField || !this.selectedOperator) return false;
    if (this.selectedField.type === 'autocomplete' && !this.materialSelected) return false;
    if (!this.hasValue(this.filterValue)) return false;
    if (this.isBetween() && !this.hasValue(this.filterValueTo)) return false;
    return true;
  }

  onOperatorChange(): void {
    this.filterValue = '';
    this.filterValueTo = '';
    this.materialSelected = false;
  }

  addFilter(): void {
    if (!this.canAddFilter() || !this.selectedField) return;

    const opLabel = this.selectedField.operators.find(o => o.key === this.selectedOperator)?.label ?? this.selectedOperator;

    let displayVal: string;
    if (this.selectedField.type === 'select') {
      const optionsMap: Record<string, { id: number; name: string }[]> = {
        jobType:        this.jobTypeOptions,
        testType:       this.testTypeOptions,
        serviceCompany: this.serviceCompanyOptions,
      };
      const opts = optionsMap[this.selectedField.key] ?? [];
      const opt = opts.find(o => String(o.id) === this.filterValue);
      displayVal = opt?.name ?? this.filterValue;
    } else if (this.isBetween()) {
      displayVal = this.filterValue + ' - ' + this.filterValueTo;
    } else {
      displayVal = this.filterValue;
    }

    this.activeFilters.push({
      field: this.selectedField,
      operator: this.selectedOperator,
      operatorLabel: opLabel,
      value: String(this.filterValue).trim(),
      valueTo: this.isBetween() ? String(this.filterValueTo).trim() || undefined : undefined,
      displayValue: displayVal,
    });

    this.selectedField = null;
    this.selectedOperator = '';
    this.filterValue = '';
    this.filterValueTo = '';
  }

  close(): void {
    this.dialogRef.close();
  }

  openProject(projectId: number, receiveDate: number): void {
    let receivedYear = new Date(receiveDate).getFullYear();
    this.dialogRef.close({ projectId, receivedYear });  
  }

  clearRow(): void {
    this.selectedField = null;
    this.selectedOperator = '';
    this.filterValue = '';
    this.filterValueTo = '';
    this.activeFilters = [];
    this.results = [];
    this.searched = false;
    this.errorMessage = null;
    this.materialSuggestions = [];
    this.showSuggestions = false;
  }

  removeFilter(index: number): void {
    this.activeFilters.splice(index, 1);
    if (this.activeFilters.length === 0) {
      this.results = [];
      this.searched = false;
    }
  }

  search(): void {
    if (this.activeFilters.length === 0) return;
    this.errorMessage = null;
    this.isSearching = true;
    this.searched = false;

    const request = this.buildRequest();
    this.toolsService.searchProjects(request).subscribe({
      next: (data) => {
        this.results = data;
        this.isSearching = false;
        this.searched = true;
      },
      error: (err: HttpErrorResponse) => {
        this.errorMessage = err.error?.detail ?? 'An error occurred while searching.';
        this.isSearching = false;
      },
    });
  }

  private buildRequest(): ProjectSearchRequest {
    const req: ProjectSearchRequest = {};

    for (const f of this.activeFilters) {
      const k  = f.field.key;
      const op = f.operator;
      const v  = f.value;
      const v2 = f.valueTo ?? '';

      switch (k) {
        case 'projectName':    req.projectName  = this.encodeText(op, v); break;
        case 'wellLocation':   req.wellLocation = this.encodeText(op, v); break;
        case 'wellName':       req.wellName     = this.encodeText(op, v); break;
        case 'pipeSize':       req.pipeSize     = this.encodeText(op, v); break;
        case 'rigName':        req.rigName      = this.encodeText(op, v); break;
        case 'jobType':          req.jobTypeId     = +v; break;
        case 'testType':         req.testType         = +v; break;
        case 'serviceCompany':   req.serviceCompanyId = +v; break;
        case 'material':       req.materialName = v; break;
        case 'md':             this.applyNumRange(op, v, v2, x => req.mdFrom = x,   x => req.mdTo = x);   break;
        case 'tvd':            this.applyNumRange(op, v, v2, x => req.tvdFrom = x,  x => req.tvdTo = x);  break;
        case 'bhct':           this.applyNumRange(op, v, v2, x => req.bhctFrom = x, x => req.bhctTo = x); break;
        case 'bhst':           this.applyNumRange(op, v, v2, x => req.bhstFrom = x, x => req.bhstTo = x); break;
        case 'finalPressure':  this.applyNumRange(op, v, v2, x => req.finalPressureFrom = x, x => req.finalPressureTo = x); break;
        case 'density':        this.applyNumRange(op, v, v2, x => req.densityFrom = x, x => req.densityTo = x); break;
        case 'thickeningTime': this.applyNumRange(op, v, v2, x => req.thickeningTimeFrom = x, x => req.thickeningTimeTo = x); break;
        case 'receiveDate':    this.applyDateRange(op, v, v2, x => req.receivedFrom = x, x => req.receivedTo = x); break;
        case 'dueDate':        this.applyDateRange(op, v, v2, x => req.dueDateFrom = x,  x => req.dueDateTo = x);  break;
      }
    }

    return req;
  }

  private applyNumRange(op: string, v: string, v2: string, setFrom: (n: number) => void, setTo: (n: number) => void): void {
    if (op === 'is')                      { setFrom(+v); setTo(+v); }
    else if (op === 'gt' || op === 'gte')   setFrom(+v);
    else if (op === 'lt' || op === 'lte')   setTo(+v);
    else                                    { setFrom(+v); setTo(+v2); } // between / betweenEx
  }

  private applyDateRange(op: string, v: string, v2: string, setFrom: (s: string) => void, setTo: (s: string) => void): void {
    if (op === 'is')                      { setFrom(v); setTo(v); }
    else if (op === 'gt' || op === 'gte')   setFrom(v);
    else if (op === 'lt' || op === 'lte')   setTo(v);
    else                                    { setFrom(v); setTo(v2); } // between / betweenEx
  }

  /** Prefix "eq:" signals exact match to the backend; raw value = contains */
  private encodeText(op: string, value: string): string {
    return op === 'is' ? 'eq:' + value : value;
  }
  
  loadJobTypes(): void {
    this.jobTypesLoading = true;
    this.jobTypesError = null;
    this.lookupService.getJobTypes().pipe(retry(1)).subscribe({
      next: (types) => {
        //console.log('[SearchComponent] Job types loaded:', types);
        this.jobTypeOptions = types;
        this.jobTypesLoading = false;
      },
      error: (err) => {
        //console.error('[SearchComponent] Failed to load job types:', err);
        this.jobTypesError = `Failed to load (${err?.status ?? err?.message ?? 'unknown error'}). Is the API running?`;
        this.jobTypesLoading = false;
      }
    });
  }

  loadTestTypes(): void {
    this.testTypesLoading = true;
    this.testTypesError = null;
    this.lookupService.getTestTypes().pipe(retry(1)).subscribe({
      next: (types) => {
        //console.log('[SearchComponent] Test types loaded:', types);
        this.testTypeOptions = types;
        this.testTypesLoading = false;
      },
      error: (err) => {
        //console.error('[SearchComponent] Failed to load test types:', err);
        this.testTypesError = `Failed to load (${err?.status ?? err?.message ?? 'unknown error'}). Is the API running?`;
        this.testTypesLoading = false;
      }
    });
  }

  loadServiceCompanies(): void {
    this.serviceCompaniesLoading = true;
    this.serviceCompaniesError = null;
    this.lookupService.getServiceCompanies().pipe(retry(1)).subscribe({
      next: (companies) => {
        this.serviceCompanyOptions = companies;
        this.serviceCompaniesLoading = false;
      },
      error: (err) => {
        //console.error('[SearchComponent] Failed to load service companies:', err);
        this.serviceCompaniesError = `Failed to load (${err?.status ?? err?.message ?? 'unknown error'}). Is the API running?`;
        this.serviceCompaniesLoading = false;
      }
    });
  }

  loadMaterials(): void {
     this.lookupService.getMaterials().subscribe({
        next: list => {
          this.allMaterials = list
            .map(m => String((m as any).name ?? (m as any).materialName ?? '').trim())
            .filter(Boolean);
        },
        error: () => { /* silently ignore; suggestions just won't show */ }
      });
  }
}
